import {
	PropsWithChildren,
	ReactElement,
	useState
} from "react";
import { Dialog } from "../Dialog/Dialog";
import { localize } from "client_legacy/ndaw/culture";
import { useDataInputDialog, useIsMounted } from "client/Hooks";
import { Toolbar } from "../Toolbar/Toolbar";
import { renderReactDialog } from "../DialogRenderer/DialogRenderer";
import { deferred } from "client/Misc/utils";

export type ConfirmationDialogProps = {
	title: string | ReactElement;
	confirmText: string;
	onConfirmation(): void | Promise<void>;
	cancelText?: string;
	onCancellation(): void;
	trackingId: string;
};

export const ConfirmationDialog = (props: PropsWithChildren<ConfirmationDialogProps>) => {
	const {
		title,
		onConfirmation,
		onCancellation,
		confirmText,
		cancelText = localize("_Cancel"),
		children,
		trackingId
	} = props;

	const [hasBusyIndicator, setHasBusyIndicator] = useState<boolean>(false);
	const isMounted: () => boolean = useIsMounted();

	const confirmationHandler = async(): Promise<void> => {
		setHasBusyIndicator(true);
		await onConfirmation();
		if(isMounted()){
			setHasBusyIndicator(false);
		}
	};

	return (
		<Dialog
			trackingId={trackingId}
			hasBusyIndicator={hasBusyIndicator}
		>
			<Dialog.Header title={title} />
			<Dialog.Body>
				<p>{children}</p>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button kind="primary" onClick={confirmationHandler} text={confirmText} trackingId="confirm" />
					<Toolbar.Button kind="secondary" onClick={onCancellation} text={cancelText} trackingId="cancel" />
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

ConfirmationDialog.displayName = "ConfirmationDialog";

export type ConfirmationDialogInitialData = Omit<
	PropsWithChildren<ConfirmationDialogProps>,
	"onConfirmation" | "onCancellation"
>;

export function useConfirmationDialog(): [
	element: ReactElement | null,
	open: (props: ConfirmationDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onCancellation, onConfirmation) => (
			<ConfirmationDialog
				{...props}
				onConfirmation={onConfirmation}
				onCancellation={onCancellation}
			/>
		)
	);
}

export async function showConfirmationDialog(
	props: Omit<
		ConfirmationDialogProps,
		"onConfirmation" | "onCancellation"
	> & {
		message: string;
		onConfirmation?(): Promise<void | boolean> | void | boolean;
	}
): Promise<TryResult<void>> {
	const result = deferred<TryResult<void>>();

	renderReactDialog(
		onClose => (
			<ConfirmationDialog
				{...props}
				onCancellation={
					(): void => {
						result.resolve({
							wasSuccessful: false
						});

						onClose();
					}
				}
				onConfirmation={
					async (): Promise<void> => {

						const shouldClose: boolean = await props.onConfirmation?.() ?? true;

						if (shouldClose) {
							result.resolve({
								wasSuccessful: true,
								result: undefined
							});

							onClose();
						}
					}
				}
			>
				{props.message}
			</ConfirmationDialog>
		)
	);

	return result.promise;
}